import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import type { ResourceSeries } from '../../hooks/useResourceSeries';
import { SeriesLockedOverlay } from './SeriesLockedOverlay';

interface Props {
  series: ResourceSeries;
  isLocked: boolean;
  onUpgradeClick: () => void;
}

export function SeriesCard({ series, isLocked, onUpgradeClick }: Props) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (isLocked) { onUpgradeClick(); return; }
    navigate(`/dashboard/series-library/${series.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="relative group cursor-pointer rounded-xl overflow-hidden bg-sand/10 dark:bg-slate/10
                 border border-sand/20 dark:border-slate/20 hover:border-electric/40 transition-all
                 hover:shadow-lg hover:-translate-y-0.5"
      style={{ aspectRatio: '16/9' }}
    >
      {/* Cover art */}
      {series.cover_art_url
        ? <img src={series.cover_art_url} alt={series.title} className="absolute inset-0 w-full h-full object-cover" />
        : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-electric/20 to-electric/5">
            <BookOpen className="h-12 w-12 text-electric/30" />
          </div>
        )
      }

      {/* Gradient overlay for text */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

      {/* Meta */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <h3 className="text-sm font-semibold text-white leading-tight line-clamp-2 mb-1.5">
          {series.title}
        </h3>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] px-1.5 py-0.5 bg-white/15 rounded-full text-white/80">
            {series.week_count} {series.week_count === 1 ? 'week' : 'weeks'}
          </span>
          {series.book_of_bible && (
            <span className="text-[10px] px-1.5 py-0.5 bg-electric/30 rounded-full text-white/90">
              {series.book_of_bible}
            </span>
          )}
          {series.tags.slice(0, 1).map(tag => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded-full text-white/70">
              {tag}
            </span>
          ))}
        </div>
      </div>

      {isLocked && <SeriesLockedOverlay onUpgradeClick={onUpgradeClick} />}
    </div>
  );
}
